import React, { useState } from 'react';
import { BarChart3, PieChart, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';


interface ExpandedCards {
  [key: number]: boolean;
}

export default function ChartingPanel(response:any) {
  const [expandedCards, setExpandedCards] = useState<ExpandedCards>({});

  


  const toggleCard = (index: number): void => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getChartIcon = (type: string): JSX.Element => {
    if (type.toLowerCase().includes('bar')) {
      return <BarChart3 className="w-2 h-2" />;
    } else if (type.toLowerCase().includes('pie')) {
      return <PieChart className="w-2 h-2" />;
    }
    return <BarChart3 className="w-2 h-2" />;
  };

  const getChartColor = (type: string): string => {
    if (type.toLowerCase().includes('bar')) {
      return 'bg-blue-500';
    } else if (type.toLowerCase().includes('pie')) {
      return 'bg-purple-500';
    }
    return 'bg-gray-500';
  };

  return (
     <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      
        {/* Header */}
        <div className="p-2 border-b border-gray-300 mb-2">
         
            <div>
              <h1 className="text-md font-bold text-gray-800 ">Chart Configuration Response</h1>
              <p className="text-xs text-gray-600">Analysis of chart recommendations and settings</p>
            </div>
            <div className="flex gap-2 mt-2">
              {response.response.branding_applied && (
                <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-lg">
                  <CheckCircle2 className="w-2 h-2" />
                  <span className="text-xs font-medium">Branding Applied</span>
                </div>
              )}
              {response.response.warnings.length === 0 && (
                <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">
                  <CheckCircle2 className="w-2 h-2" />
                  <span className="text-xs font-medium">No Warnings</span>
                </div>
              )}
            
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4 px-2">
          <div className="bg-white rounded-lg shadow-md p-2">
            <div className="text-md font-bold text-blue-600 mb-1">
              {response.response.chart_configs.length}
            </div>
            <div className="text-gray-600 text-sm">Total Charts</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-2">
            <div className="text-md font-bold text-purple-600 mb-1">
              {response.response.warnings.length}
            </div>
            <div className="text-gray-600 text-sm">Warnings</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-2">
            <div className="text-md font-bold text-green-600 mb-1">
              {response.response.chart_configs.filter((c:any) => c.segments_visible).length}
            </div>
            <div className="text-gray-600 text-sm">Visible Segments</div>
          </div>
        </div>

        {/* Chart Configurations */}
        <div className="space-y-4 px-2">
          <h2 className="text-md font-bold text-gray-800 mb-4">Chart Configurations</h2>
          
          {response.response.chart_configs.map((config:any, index:any) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
              <div className="p-2">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`${getChartColor(config.recommended_type)} p-1 rounded-lg text-white`}>
                      {getChartIcon(config.recommended_type)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-800 ">
                        {config.question_id}
                      </h3>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium text-gray-600">
                          Recommended Type:
                        </span>
                        <span className={`px-2 py-1 rounded-full text-sm font-medium text-white ${getChartColor(config.recommended_type)}`}>
                          {config.recommended_type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleCard(index)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {expandedCards[index] ? <EyeOff className="w-2 h-2" /> : <Eye className="w-2 h-2" />}
                  </button>
                </div>

                {expandedCards[index] && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex items-center justify-between p-1 bg-gray-50 rounded-lg">
                        <span className="text-xs font-medium text-gray-600">Orientation</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.is_vertical ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                          {config.is_vertical ? 'Vertical' : 'Horizontal'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-1 bg-gray-50 rounded-lg">
                        <span className="text-xs font-medium text-gray-600">Show Values</span>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          {config.show_values}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-1 bg-gray-50 rounded-lg">
                        <span className="text-xs font-medium text-gray-600">Segments Visible</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.segments_visible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {config.segments_visible ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {!expandedCards[index] && (
                  <div className="mt-3 flex gap-1 flex-wrap">
                    <span className="text-xs bg-gray-100 text-gray-600 px-1 py-1 rounded">
                      {config.is_vertical ? 'Vertical' : 'Horizontal'}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-1 py-1 rounded">
                      Values: {config.show_values}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-1 py-1 rounded">
                      Segments: {config.segments_visible ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Warnings Section */}
        {response.response.warnings.length > 0 ? (
          <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800 mb-2">Warnings</h3>
                <ul className="list-disc list-inside space-y-1 text-yellow-700 text-sm">
                  {response.response.warnings.map((warning:any, index:any) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="my-3 bg-green-50 border-l-4 border-green-400 p-2 mx-2 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-2 h-2 text-green-600" />
              <p className="text-green-800 text-xs font-medium">All configurations are valid with no warnings</p>
            </div>
          </div>
        )}

       
      
    </div>
  );
};